import { Badge, Button, Card, CardBody, CardHeader, FormField, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BinContent, PalletDetail } from '@warehouse/shared';
import { ArrowRight, PackageSearch, ScanLine } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { PageHeader } from '../../../components/PageHeader';
import { formatQty } from '../../../lib/format';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';

type Tab = 'pallet' | 'goods';

/**
 * Internal movements (spec §5.6) — the web twin of the scanner flows.
 *
 * Both forms POST the same commands the Android app emits (`move.pallet`,
 * `move.goods`), so a move made at a desk and a move made in an aisle produce
 * the same ledger rows and the same audit trail.
 */
export function MovesPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('pallet');

  return (
    <>
      <PageHeader title={t('warehouseCore:moves.title')} subtitle={t('warehouseCore:moves.subtitle')} />

      <div className="mb-5 inline-flex rounded-full border border-surface-border p-1">
        {(['pallet', 'goods'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setTab(value);
            }}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              tab === value ? 'bg-brand-600 text-white' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t(value === 'pallet' ? 'warehouseCore:moves.movePallet' : 'warehouseCore:moves.moveGoods')}
          </button>
        ))}
      </div>

      {tab === 'pallet' ? <MovePalletForm initialCode={searchParams.get('lpn') ?? ''} /> : <MoveGoodsForm />}
    </>
  );
}

// ---------------------------------------------------------------------------

function MovePalletForm({ initialCode }: { initialCode: string }): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const queryClient = useQueryClient();
  const [code, setCode] = useState(initialCode);
  const [lookupCode, setLookupCode] = useState(initialCode);
  const [toBinCode, setToBinCode] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    setCode(initialCode);
    setLookupCode(initialCode);
  }, [initialCode]);

  const pallet = useQuery({
    queryKey: ['warehouse', 'pallet-lookup', lookupCode],
    queryFn: () => warehouseApi.lookupPallet(lookupCode),
    enabled: lookupCode.trim().length > 0,
    retry: false,
  });

  const move = useMutation({
    mutationFn: (palletData: PalletDetail) =>
      warehouseApi.movePallet({
        palletId: palletData.id,
        toBinCode: toBinCode.trim().toUpperCase(),
        observedFromBinId: palletData.currentBinId,
        note: note.trim() || null,
      }),
    onSuccess: (result) => {
      if (result.verdict === 'healed') toastInfo(t('warehouseCore:moves.healed'));
      toastSuccess(
        t('warehouseCore:moves.palletMoved', {
          lpn: result.result?.lpn ?? '',
          binCode: result.result?.toBinCode ?? toBinCode,
        }),
      );
      setToBinCode('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      void pallet.refetch();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (pallet.data && toBinCode.trim()) move.mutate(pallet.data);
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <h2 className="font-display text-base font-semibold text-ink">
            {t('warehouseCore:moves.movePallet')}
          </h2>
        </CardHeader>
        <CardBody>
          <form className="flex flex-col gap-4" onSubmit={submit}>
            <div className="flex items-end gap-2">
              <FormField
                className="flex-1"
                label={t('warehouseCore:moves.palletCode')}
                hint={t('warehouseCore:moves.palletCodeHint')}
                value={code}
                autoFocus
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                onBlur={() => {
                  setLookupCode(code.trim());
                }}
                onKeyDown={(e) => {
                  // Scanners emit the code followed by Enter — treat that as
                  // "look it up", never as "submit the whole move".
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setLookupCode(code.trim());
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                icon={<ScanLine />}
                onClick={() => {
                  setLookupCode(code.trim());
                }}
              >
                {t('common:actions.search')}
              </Button>
            </div>

            {pallet.isError ? (
              <Surface variant="subtle" className="p-3 text-sm text-danger-600">
                {t('warehouseCore:pallets.empty')}
              </Surface>
            ) : null}

            {pallet.data ? (
              <Surface variant="subtle" className="flex flex-col gap-2 p-3">
                <p className="text-sm text-ink">
                  {t('warehouseCore:moves.palletFound', {
                    lpn: pallet.data.lpn,
                    binCode: pallet.data.currentBinCode ?? t('warehouseCore:moves.palletNotPlaced'),
                  })}
                </p>
                <div className="flex flex-wrap gap-1">
                  {pallet.data.contents.length === 0 ? (
                    <Badge tone="warning">{t('warehouseCore:pallets.noContents')}</Badge>
                  ) : (
                    pallet.data.contents.map((line) => (
                      <Badge key={line.stockId} tone="neutral">
                        {line.articleNo} · {formatQty(line.qtyBase)}
                      </Badge>
                    ))
                  )}
                </div>
              </Surface>
            ) : null}

            <FormField
              label={t('warehouseCore:moves.toBin')}
              hint={t('warehouseCore:moves.toBinHint')}
              value={toBinCode}
              onChange={(e) => {
                setToBinCode(e.target.value.toUpperCase());
              }}
            />
            <FormField
              label={t('warehouseCore:moves.note')}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />

            <Button
              type="submit"
              variant="primary"
              icon={<ArrowRight />}
              loading={move.isPending}
              disabled={!pallet.data || toBinCode.trim().length === 0}
            >
              {t('warehouseCore:moves.submit')}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function MoveGoodsForm(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const queryClient = useQueryClient();
  const [fromBinCode, setFromBinCode] = useState('');
  const [lookupBin, setLookupBin] = useState('');
  const [selected, setSelected] = useState<BinContent | null>(null);
  const [qty, setQty] = useState('');
  const [toBinCode, setToBinCode] = useState('');
  const [note, setNote] = useState('');

  const bin = useQuery({
    queryKey: ['warehouse', 'bin-lookup', lookupBin],
    queryFn: async () => {
      const found = await warehouseApi.lookupBin(lookupBin);
      return warehouseApi.getBin(found.id);
    },
    enabled: lookupBin.trim().length > 0,
    retry: false,
  });

  const move = useMutation({
    mutationFn: () =>
      warehouseApi.moveGoods({
        articleId: selected!.articleId,
        lotId: selected!.lotId,
        palletId: selected!.palletId,
        fromBinId: bin.data!.id,
        toBinCode: toBinCode.trim().toUpperCase(),
        qtyBase: qty,
        note: note.trim() || null,
      }),
    onSuccess: (result) => {
      toastSuccess(
        t('warehouseCore:moves.goodsMoved', {
          qty: formatQty(result.result?.qtyBase ?? qty),
          binCode: toBinCode.toUpperCase(),
        }),
      );
      setSelected(null);
      setQty('');
      setToBinCode('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      void bin.refetch();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <h2 className="font-display text-base font-semibold text-ink">
            {t('warehouseCore:moves.pickSource')}
          </h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          <div className="flex items-end gap-2">
            <FormField
              className="flex-1"
              label={t('warehouseCore:moves.fromBin')}
              hint={t('warehouseCore:moves.sourceHint')}
              value={fromBinCode}
              onChange={(e) => {
                setFromBinCode(e.target.value.toUpperCase());
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setLookupBin(fromBinCode.trim());
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              icon={<PackageSearch />}
              onClick={() => {
                setLookupBin(fromBinCode.trim());
              }}
            >
              {t('common:actions.search')}
            </Button>
          </div>

          {bin.isError ? (
            <Surface variant="subtle" className="p-3 text-sm text-danger-600">
              {t('warehouseCore:bins.empty')}
            </Surface>
          ) : null}

          {bin.data ? (
            bin.data.contents.length === 0 ? (
              <Surface variant="subtle" className="p-3 text-sm text-ink-muted">
                {t('warehouseCore:bins.noContents')}
              </Surface>
            ) : (
              <ul className="flex flex-col gap-2">
                {bin.data.contents.map((line) => (
                  <li key={line.stockId}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelected(line);
                        setQty(line.qtyBase);
                      }}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                        selected?.stockId === line.stockId
                          ? 'border-brand-500 bg-brand-500/5'
                          : 'border-surface-border hover:border-ink/30'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-ink">
                          <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>{' '}
                          {line.articleName}
                        </p>
                        <p className="truncate text-xs text-ink-muted">
                          {line.lotNo ? `${line.lotNo} · ` : ''}
                          {line.lpn ?? t('warehouseCore:stock.loose')}
                        </p>
                      </div>
                      <span className="shrink-0 font-mono text-sm">{formatQty(line.qtyBase)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-display text-base font-semibold text-ink">
            {t('warehouseCore:moves.moveGoods')}
          </h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          {selected ? (
            <Surface variant="subtle" className="p-3 text-sm">
              <p className="text-ink">
                {selected.articleNo} · {selected.articleName}
              </p>
              <p className="text-xs text-ink-muted">
                {t('warehouseCore:moves.available', { qty: formatQty(selected.qtyBase) })}
              </p>
            </Surface>
          ) : (
            <Surface variant="subtle" className="p-3 text-sm text-ink-muted">
              {t('warehouseCore:moves.noSource')}
            </Surface>
          )}

          <FormField
            label={t('warehouseCore:common.quantityBase')}
            type="number"
            inputMode="decimal"
            step="0.001"
            min="0"
            value={qty}
            disabled={!selected}
            onChange={(e) => {
              setQty(e.target.value);
            }}
          />
          <FormField
            label={t('warehouseCore:moves.toBin')}
            hint={t('warehouseCore:moves.toBinHint')}
            value={toBinCode}
            disabled={!selected}
            onChange={(e) => {
              setToBinCode(e.target.value.toUpperCase());
            }}
          />
          <FormField
            label={t('warehouseCore:moves.note')}
            value={note}
            disabled={!selected}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />

          <Button
            variant="primary"
            icon={<ArrowRight />}
            loading={move.isPending}
            disabled={!selected || !bin.data || toBinCode.trim().length === 0 || qty.trim().length === 0}
            onClick={() => {
              move.mutate();
            }}
          >
            {t('warehouseCore:moves.submit')}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
